import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { WhatsappTab, ChatFilter } from "../types/whatsapp.types";

export interface WhatsappState {
  activeTab: WhatsappTab;
  activeContactId: number | null;
  isInfoPanelOpen: boolean;
  isTemplatePickerOpen: boolean;
  chatFilter: ChatFilter;
  searchQuery: string;
  activeAccountId: string;
  /** The inbox shows only this account's chats (`meta:<id>` / `source:<id>`); null = all accounts. */
  inboxAccountKey: string | null;
  followupModalOpen: boolean;
  followupModalLeadId: number | null;
  automationBuilderOpen: boolean;
  editingAutomationId: number | null;
  broadcastBuilderOpen: boolean;
  templateDetailId: number | null;
  pendingChatLeadId: number | null;
}

const initialState: WhatsappState = {
  activeTab: "inbox",
  activeContactId: null,
  isInfoPanelOpen: false,
  isTemplatePickerOpen: false,
  chatFilter: "all",
  searchQuery: "",
  activeAccountId: "",
  inboxAccountKey: null,
  followupModalOpen: false,
  followupModalLeadId: null,
  automationBuilderOpen: false,
  editingAutomationId: null,
  broadcastBuilderOpen: false,
  templateDetailId: null,
  pendingChatLeadId: null,
};

const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<WhatsappTab>) {
      state.activeTab = action.payload;
    },
    setActiveContact(state, action: PayloadAction<number | null>) {
      state.activeContactId = action.payload;
      state.isInfoPanelOpen = false;
      state.isTemplatePickerOpen = false;
    },
    toggleInfoPanel(state) {
      state.isInfoPanelOpen = !state.isInfoPanelOpen;
    },
    setInfoPanelOpen(state, action: PayloadAction<boolean>) {
      state.isInfoPanelOpen = action.payload;
    },
    toggleTemplatePicker(state) {
      state.isTemplatePickerOpen = !state.isTemplatePickerOpen;
    },
    setTemplatePickerOpen(state, action: PayloadAction<boolean>) {
      state.isTemplatePickerOpen = action.payload;
    },
    setChatFilter(state, action: PayloadAction<ChatFilter>) {
      state.chatFilter = action.payload;
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    setActiveAccount(state, action: PayloadAction<string>) {
      state.activeAccountId = action.payload;
    },
    setInboxAccount(state, action: PayloadAction<string | null>) {
      state.inboxAccountKey = action.payload;
    },
    openFollowupModal(state, action: PayloadAction<number>) {
      state.followupModalOpen = true;
      state.followupModalLeadId = action.payload;
    },
    closeFollowupModal(state) {
      state.followupModalOpen = false;
      state.followupModalLeadId = null;
    },
    openAutomationBuilder(state, action: PayloadAction<number | null>) {
      state.automationBuilderOpen = true;
      state.editingAutomationId = action.payload;
    },
    closeAutomationBuilder(state) {
      state.automationBuilderOpen = false;
      state.editingAutomationId = null;
    },
    toggleBroadcastBuilder(state) {
      state.broadcastBuilderOpen = !state.broadcastBuilderOpen;
    },
    setTemplateDetailId(state, action: PayloadAction<number | null>) {
      state.templateDetailId = action.payload;
    },
    openChatForLead(state, action: PayloadAction<number>) {
      state.pendingChatLeadId = action.payload;
      state.activeTab = "inbox";
    },
    clearPendingChatLead(state) {
      state.pendingChatLeadId = null;
    },
  },
});

export const {
  setActiveTab,
  setActiveContact,
  toggleInfoPanel,
  setInfoPanelOpen,
  toggleTemplatePicker,
  setTemplatePickerOpen,
  setChatFilter,
  setSearchQuery,
  setActiveAccount,
  setInboxAccount,
  openFollowupModal,
  closeFollowupModal,
  openAutomationBuilder,
  closeAutomationBuilder,
  toggleBroadcastBuilder,
  setTemplateDetailId,
  openChatForLead,
  clearPendingChatLead,
} = whatsappSlice.actions;

export default whatsappSlice.reducer;
